import React, { useEffect, useState } from 'react';
import { formatDistance } from 'date-fns';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';
import toast from 'react-hot-toast';

export default function PhotoReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');

  const statusTabs = [
    { value: 'pending', label: 'Pending' },
    { value: 'reviewed', label: 'Hidden' },
    { value: 'dismissed', label: 'Dismissed' },
  ];

  const loadReports = async (status) => {
    try {
      setLoading(true);
      const service = client.service('admin/photo-report');
      const result = await service.find({
        query: { status }
      });
      setReports(result.data || result);
    } catch (error) {
      console.error('Error loading photo reports:', error);
      toast.error('Failed to load photo reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports(statusFilter);
  }, [statusFilter]);

  const handleDismiss = async (report) => {
    try {
      const service = client.service('admin/photo-report');
      await service.patch(report.id, { status: 'dismissed' });
      setReports(reports.filter(r => r.id !== report.id));
      toast.success('Report dismissed');
    } catch (error) {
      console.error('Error dismissing report:', error);
      toast.error('Failed to dismiss report');
    }
  };

  const handleHidePhoto = async (report) => {
    const confirmed = window.confirm('Are you sure you want to hide this photo? This action will remove it from the public gallery.');
    if (!confirmed) return;

    try {
      const service = client.service('admin/photo-report');
      await service.patch(report.id, { status: 'reviewed', hidePhoto: true });
      setReports(reports.filter(r => r.id !== report.id));
      toast.success('Photo hidden from gallery');
    } catch (error) {
      console.error('Error hiding photo:', error);
      toast.error('Failed to hide photo');
    }
  };

  return (
    <Page title="Photo Reports">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[
            { name: 'Reports', href: `/admin/reports` },
            { name: 'Photo Reports' }
          ]} />
        </nav>
      </div>

      <div className="py-5">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Photo Reports</h1>

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-6">
            {statusTabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value)}
                className={`pb-3 px-1 text-sm font-medium border-b-2 ${
                  statusFilter === tab.value
                    ? 'border-red-800 text-red-800'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {loading && <p>Loading...</p>}

        {!loading && reports.length === 0 && (
          <div className="bg-white shadow rounded-lg p-6">
            <p className="text-gray-500">No {statusFilter} photo reports</p>
          </div>
        )}

        {!loading && reports.length > 0 && (
          <div className="flex flex-wrap gap-4">
            {reports.map((report) => (
              <div key={report.id} className="bg-white shadow rounded-lg overflow-hidden w-full sm:w-[calc(50%-0.5rem)]">
                <div className="flex gap-3 p-4">
                  <img
                    src={`${process.env.REACT_APP_ATTACHMENT_URL}/${report.attachment?.path}?width=200`}
                    alt="Reported photo"
                    className="w-24 h-24 flex-shrink-0 object-cover rounded cursor-pointer"
                    onClick={() => setSelectedPhoto(report.attachment)}
                  />
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="text-sm">
                      <span className="text-gray-500">Owner: </span>
                      <span className="font-medium text-gray-900">
                        {report.owner?.firstName} {report.owner?.lastName}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500">Reporter: </span>
                      <span className="font-medium text-gray-900">
                        {report.reporter?.firstName} {report.reporter?.lastName}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 truncate">
                      {report.reason || 'No reason provided'}
                    </div>
                    <div className="text-xs text-gray-500">
                      Reported {formatDistance(new Date(report.createdAt), new Date(), { addSuffix: true })}
                    </div>
                    {report.resolvedBy && (
                      <div className="text-xs text-gray-500">
                        {report.status === 'reviewed' ? 'Hidden' : 'Dismissed'} by {report.resolvedBy.firstName} {report.resolvedBy.lastName}
                        {report.resolvedAt && (
                          <span title={new Date(report.resolvedAt).toLocaleString()}>
                            {` ${formatDistance(new Date(report.resolvedAt), new Date(), { addSuffix: true })}`}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {statusFilter === 'pending' && (
                  <div className="flex border-t border-gray-200 divide-x divide-gray-200">
                    <button
                      onClick={() => handleDismiss(report)}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Dismiss
                    </button>
                    <button
                      onClick={() => handleHidePhoto(report)}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
                    >
                      Hide Photo
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] mx-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute -top-10 right-0 text-white text-sm hover:text-gray-300"
            >
              Close
            </button>
            <img
              src={`${process.env.REACT_APP_ATTACHMENT_URL}/${selectedPhoto.path}`}
              alt="Reported photo"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </Page>
  );
}
